import type { LoaderFunctionArgs, ShouldRevalidateFunction } from 'react-router-dom';
import {
  contracts,
  DashboardPeriodSchema,
  DEFAULT_DASHBOARD_PERIOD,
  type DashboardPeriod,
  type GetDashboardAttentionResponse,
  type GetDashboardFunnelResponse,
  type GetDashboardNewRecordsResponse,
  type GetDashboardSetupResponse,
  type GetDashboardTeamResponse,
  type MeResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { loadWorkspaceContext } from '@/lib/auth/workspace-context';
import { nonRouteRules } from '@/lib/permissions/non-route-rules';
import { mayCall } from '@/lib/permissions/route-gate';
import { DASHBOARD_LINK_PARAMS } from './dashboard-link';

export type DashboardStandort = { readonly id: string; readonly name: string };

export type DashboardLoaderData =
  | { readonly kind: 'DENIED'; readonly displayName: string }
  | {
      readonly kind: 'READY';
      readonly slug: string;
      readonly workspaceId: string;
      // The Standort the funnel was read with: the same read is repeated when the period changes.
      readonly standortId: string | null;
      readonly period: DashboardPeriod;
      // The Standorte the member may switch to; the prompt offers one button per entry.
      readonly standorte: ReadonlyArray<DashboardStandort>;
      // `null` is a failed read, rendered as that block's failure — never as an empty block (BL-058).
      readonly funnel: Promise<GetDashboardFunnelResponse | null>;
      // Ignores the period. `null` is a failed read (BL-058).
      readonly attention: Promise<GetDashboardAttentionResponse | null>;
      // The two tables ignore the period, so a period switch never re-reads them. `null` is a failed read.
      readonly newRecords: Promise<GetDashboardNewRecordsResponse | null>;
      // Ignores the period too. `null` is a failed read, which must stay distinguishable from `{ items: [] }` (BL-058).
      readonly setup: Promise<GetDashboardSetupResponse | null>;
      // Ignores the period. `null` is a failed read (BL-058).
      readonly team: Promise<GetDashboardTeamResponse | null>;
      // The members screen redirects a caller without this grant home, so the „offen“ figure links only when it holds.
      readonly canOpenMembers: boolean;
    };

function formatDisplayName(me: MeResponse): string {
  const parts = [me.user.firstName, me.user.lastName].filter((p): p is string => p !== null && p.length > 0);

  return parts.length > 0 ? parts.join(' ') : me.user.email;
}

export async function readFunnel(
  workspaceId: string,
  standortId: string | null,
  period: DashboardPeriod,
): Promise<GetDashboardFunnelResponse | null> {
  try {
    const result = await apiClient.dashboard.funnel({
      pathParams: { workspaceId },
      query: { ...(standortId !== null ? { standortId } : {}), period },
    });

    return result.ok ? result.body : null;
  } catch {
    return null;
  }
}

export async function readAttention(
  workspaceId: string,
  standortId: string | null,
): Promise<GetDashboardAttentionResponse | null> {
  try {
    const result = await apiClient.dashboard.attention({
      pathParams: { workspaceId },
      query: standortId !== null ? { standortId } : {},
    });

    return result.ok ? result.body : null;
  } catch {
    return null;
  }
}

export async function readNewRecords(
  workspaceId: string,
  standortId: string | null,
): Promise<GetDashboardNewRecordsResponse | null> {
  try {
    const result = await apiClient.dashboard.newRecords({
      pathParams: { workspaceId },
      query: standortId !== null ? { standortId } : {},
    });

    return result.ok ? result.body : null;
  } catch {
    return null;
  }
}

export async function readSetup(
  workspaceId: string,
  standortId: string | null,
): Promise<GetDashboardSetupResponse | null> {
  try {
    const result = await apiClient.dashboard.setup({
      pathParams: { workspaceId },
      query: standortId !== null ? { standortId } : {},
    });

    return result.ok ? result.body : null;
  } catch {
    return null;
  }
}

export async function readTeam(
  workspaceId: string,
  standortId: string | null,
): Promise<GetDashboardTeamResponse | null> {
  try {
    const result = await apiClient.dashboard.team({
      pathParams: { workspaceId },
      query: standortId !== null ? { standortId } : {},
    });

    return result.ok ? result.body : null;
  } catch {
    return null;
  }
}

/** `?period=` as the loader reads it: absent or unknown means the default. */
export function readDashboardPeriod(search: URLSearchParams): DashboardPeriod {
  const parsed = DashboardPeriodSchema.safeParse(search.get(DASHBOARD_LINK_PARAMS.period));

  return parsed.success ? parsed.data : DEFAULT_DASHBOARD_PERIOD;
}

/**
 * A period switch re-reads only the funnel, in the page (BL-011), so the loader must not run for it. Anything else —
 * `revalidate()`, a Standort switch, another parameter — keeps the default.
 */
export const dashboardShouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  const rest = (url: URL) => {
    const next = new URLSearchParams(url.searchParams);

    next.delete(DASHBOARD_LINK_PARAMS.period);
    next.sort();

    return next.toString();
  };

  const onlyPeriodChanged =
    currentUrl.pathname === nextUrl.pathname &&
    currentUrl.searchParams.get(DASHBOARD_LINK_PARAMS.period) !==
      nextUrl.searchParams.get(DASHBOARD_LINK_PARAMS.period) &&
    rest(currentUrl) === rest(nextUrl);

  return onlyPeriodChanged ? false : defaultShouldRevalidate;
};

export async function dashboardLoader({ params, request }: LoaderFunctionArgs): Promise<DashboardLoaderData> {
  const ctx = await loadWorkspaceContext(params.workspaceId);

  if (!mayCall(ctx.permissions, contracts.dashboard.funnel)) {
    return { kind: 'DENIED', displayName: formatDisplayName(ctx.me) };
  }

  const period = readDashboardPeriod(new URL(request.url).searchParams);
  const standorte = ctx.organizations
    .filter((organization) => organization.accessible !== false)
    .map(({ id, name }) => ({ id, name }));
  // With one selectable Standort the switcher is hidden, so a prompt there could not be answered.
  const only = standorte.length === 1 ? (standorte[0]?.id ?? null) : null;
  const standortId = ctx.narrowedOrganizationId ?? only;

  return {
    kind: 'READY',
    slug: ctx.workspaceSlug,
    workspaceId: ctx.workspaceId,
    standortId,
    period,
    standorte,
    funnel: readFunnel(ctx.workspaceId, standortId, period),
    attention: readAttention(ctx.workspaceId, standortId),
    newRecords: readNewRecords(ctx.workspaceId, standortId),
    setup: readSetup(ctx.workspaceId, standortId),
    team: readTeam(ctx.workspaceId, standortId),
    canOpenMembers: mayCall(ctx.permissions, nonRouteRules.membersScreen),
  };
}
